/* eslint-disable @typescript-eslint/no-explicit-any */
import { DynamicFormProps } from '@/shared/components/DynamicForm/DynamicForm'
import { buildSyncFieldsValidationSchema } from '@/features/menu-management/helpers/buildSyncFieldsValidationSchema'
import { useDishCategoriesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishCategoriesQuery'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import {
  AutoTranslateButton,
  AutoTranslateFormEffect
} from '@/features/menu-management/helpers/AutoTranslateFormEffect'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useIngredientsQuery } from '../../Ingredients/hooks/useIngredientsQuery'
import { useIngredientsColumnsConfigInToppingsDetailsFrom } from '../../Dishes/hooks/useIngredientsColumnsConfigInToppingsDetailsFrom'
import { currencySymbol } from '@/shared/utils/price'

export const useToppingFormConfig = (defaultValues?: any) => {
  const { t } = useTranslation()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''

  const { defaultLanguageOption, selectedLanguagesOptions } =
    useSelectedLanguagesQuery(currentLanguage)

  const { dishCategoriesOptions } = useDishCategoriesQuery()
  const ingredientsColumns = useIngredientsColumnsConfigInToppingsDetailsFrom()
  const { ingredientsList } = useIngredientsQuery()

  const mappedIngredientsList = useMemo(
    () =>
      ingredientsList?.map(ingredient => ({
        label: ingredient.translations?.find(
          translation => translation.languageCode === 'en'
        )?.name,
        value: ingredient.id
      })) || [],
    [ingredientsList]
  )

  const formConfig = useMemo(
    () => ({
      id: `topping-details-form-${selectedLanguagesOptions.length}`,
      defaultValues,
      FormEffects: <AutoTranslateFormEffect />,
      // Rendered by the modal in its action row rather than beside the language
      // tabs: a translation helper belongs with confirm, not wedged into the
      // form's own controls.
      footerAction: (
        <AutoTranslateButton
          defaultLanguage={defaultLanguageOption?.value || 'en'}
          allLanguages={selectedLanguagesOptions.map(o => o.value as string)}
          syncFieldNames={['toppingName', 'toppingDescription']}
        />
      ),
      fields: [
        {
          type: 'step',
          name: 'basicInfo',
          label: t('TOPPING_DETAILS_MODAL.FIRST_STEP_LABEL'),
          fields: [
            {
              name: 'translations',
              label: t('TOPPING_DETAILS_MODAL.TRANSLATIONS.DESCRIPTION'),
              type: 'label'
            },
            {
              name: 'toppingLanguage',
              type: 'tabs',
              tabs: selectedLanguagesOptions,
              isSyncFormField: true,
              hideClearIcon: true
            },
            {
              name: 'toppingName',
              label: t('DETAILS_TILE.MENU.NAME.LABEL'),
              placeholder: t('DETAILS_TILE.MENU.NAME.PLACEHOLDER'),
              type: 'text',
              focused: true,
              required: true,
              validation: buildSyncFieldsValidationSchema(
                selectedLanguagesOptions
              ),
              syncFormField: 'toppingLanguage'
            },
            {
              name: 'toppingDescription',
              label: t('DETAILS_TILE.MENU.DESCRIPTION.LABEL'),
              placeholder: t('DETAILS_TILE.MENU.DESCRIPTION.PLACEHOLDER'),
              type: 'text-area',
              focused: true,
              validation: buildSyncFieldsValidationSchema(
                selectedLanguagesOptions
              ),
              syncFormField: 'toppingLanguage'
            },
            {
              name: 'additionalInformation',
              type: 'label',
              label: t(
                'TOPPING_DETAILS_MODAL.TRANSLATIONS.ADDITIONAL_INFORMATION'
              )
            },
            {
              type: 'input-group',
              inputs: [
                {
                  name: 'price',
                  label: t('DETAILS_TILE.MENU.PRICE.LABEL'),
                  placeholder: t('DETAILS_TILE.MENU.PRICE.PLACEHOLDER'),
                  type: 'number',
                  focused: true,
                  endAdornment: currencySymbol(),
                  validation: z
                    .union([
                      z.string().transform(x =>
                        // Comma is the decimal separator people type here
                        x.replace(',', '.').replace(/[^0-9.-]+/g, '')
                      ),
                      z.number()
                    ])
                    .pipe(z.coerce.number().min(0.01).max(999999999))
                },
                {
                  name: 'dishCategories',
                  label: t('SIDEBAR_ACCORDION.CATEGORIES.TITLE'),
                  placeholder: t('SIDEBAR_ACCORDION.CATEGORIES.TITLE'),
                  focused: true,
                  type: 'multi-select',
                  options: dishCategoriesOptions
                }
              ]
            },
            {
              type: 'input-group',
              inputs: [
                {
                  name: 'toppingIngredients',
                  label: t('DETAILS_TILE.MENU.INGREDIENTS.LABEL'),
                  placeholder: t('DETAILS_TILE.MENU.INGREDIENTS.PLACEHOLDER'),
                  type: 'multi-select',
                  focused: true,
                  options: mappedIngredientsList
                },
                {
                  name: 'tax',
                  label: t('DETAILS_TILE.MENU.TAX.LABEL'),
                  placeholder: t('DETAILS_TILE.MENU.TAX.PLACEHOLDER'),
                  type: 'number',
                  focused: true,
                  validation: z
                    .preprocess(val => {
                      if (typeof val === 'string') {
                        const cleaned = val
                          .replace(',', '.')
                          .replace(/[^0-9.]+/g, '')
                        return cleaned === '' ? undefined : Number(cleaned)
                      }
                      if (typeof val === 'number') return val
                      return undefined
                    }, z.number().min(0).max(100))
                    .optional()
                }
              ]
            }
          ]
        },
        {
          type: 'step',
          label: t('TOPPING_DETAILS_MODAL.SECOND_STEP_LABEL'),
          referenceField: 'toppingIngredients',
          visibilityLogic: 'visibleIfNotEmpty',
          fields: [
            {
              name: 'ingredients',
              label: 'Ingredients by dish size',
              type: 'table',
              columns: ingredientsColumns,
              tableKey: 'PRODUCTS_TABLE',
              referenceField: 'toppingIngredients',
              visibilityLogic: 'visibleIfNotEmpty',
              dataMatrixFields: ['toppingIngredients'],
              editable: true
            }
          ]
        }
      ]
    }),
    [
      defaultValues,
      t,
      defaultLanguageOption,
      selectedLanguagesOptions,
      dishCategoriesOptions,
      ingredientsColumns,
      mappedIngredientsList
    ]
  )
  return formConfig as DynamicFormProps
}
